/**
 * The live room's rules and timer (roadmap S-2 and S-3; rulings R11, R12,
 * R19; migration 973).
 *
 * - The rules — what happens when the host's timer reaches zero, and how names
 *   are shown — are set on the chain workflow → cycle → track → session and
 *   resolved by the server (get_session_room_rules).
 * - The timer is the activity's deadline, set by the host from the server's
 *   clock (set_room_timer). Screens read seconds left from the server
 *   (get_room_timer; guests get_guest_room_timer), so a wrong clock on a
 *   phone does not matter, and follow the host's changes through
 *   session_events.
 * - When the rule is "lock", the database itself refuses an answer after
 *   zero; the screens say so before anyone tries.
 */
import { useCallback, useEffect, useState } from 'react';
import { getTypedSupabaseClient } from '../typedSupabaseClient';
import { getSafeSupabaseClient } from '../supabaseSync';
import { emitSessionEvent } from '../sessionEvents';
import { getOrCreateGuestSecret } from '../guestParticipant';
import type { RoomAnonymity, TimerAtZero } from '@/stores/prioritization/sessionPolicyStore';

export type { RoomAnonymity, TimerAtZero };

export interface RoomRules {
  timerAtZero: TimerAtZero;
  anonymity: RoomAnonymity;
  /** Workspace admins see names in the record (never on the room's screens). */
  isAdmin: boolean;
}

/** The host's quick choices. */
export const ROOM_TIMER_CHOICES = [
  { label: '3 min', seconds: 180 },
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
];

/**
 * The browser-clock end time for a timer with `secondsLeft` on the server.
 * Keeps the running clock unless the host has changed it: a poll's
 * whole-second rounding would otherwise make the display jump.
 */
export function nextTimerEnd(current: number | null, secondsLeft: number | null, nowMs: number = Date.now()): number | null {
  if (secondsLeft === null) return null;
  const next = nowMs + secondsLeft * 1000;
  return current !== null && Math.abs(current - next) < 2500 ? current : next;
}

export const DEFAULT_ROOM_RULES: RoomRules = { timerAtZero: 'tell', anonymity: 'shown', isAdmin: false };

export interface RoomTimer {
  hasTimer: boolean;
  secondsLeft: number | null;
  atZero: TimerAtZero;
}

/** The line a locked answer gets from the database (migration 973). */
export const ANSWERS_LOCKED_MESSAGE = 'Time is up: answers to this activity are locked.';

/** Whether an error from saving an answer is the timer's lock. */
export function isAnswersLockedError(message: string | null | undefined): boolean {
  return !!message && message.includes('answers to this activity are locked');
}

/** Names are kept off this screen. Live screens hide them from everyone; the record shows them to admins. */
export function namesHidden(rules: RoomRules, where: 'room' | 'record'): boolean {
  if (rules.anonymity === 'shown') return false;
  return where === 'room' ? true : !rules.isAdmin;
}

/** What a hidden person is called on a screen: their place in the list. */
export function anonymousLabel(index: number): string {
  return `Participant ${index + 1}`;
}

const asAtZero = (v: unknown): TimerAtZero => (v === 'lock' ? 'lock' : 'tell');
const asAnonymity = (v: unknown): RoomAnonymity => (v === 'hidden' || v === 'withheld' ? v : 'shown');

export function parseRoomRules(row: unknown): RoomRules | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  if (r.success === false) return null;
  return { timerAtZero: asAtZero(r.timer_at_zero), anonymity: asAnonymity(r.anonymity), isAdmin: r.is_admin === true };
}

export function parseRoomTimer(row: unknown): RoomTimer | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  if (r.success === false) return null;
  const left = typeof r.seconds_left === 'number' ? r.seconds_left : null;
  return { hasTimer: r.has_timer === true && left !== null, secondsLeft: left, atZero: asAtZero(r.at_zero) };
}

type Rpc = (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

function rpc(): Rpc {
  // Cast the client before binding: binding the typed rpc first makes TypeScript
  // expand every database function's signature (TS2589).
  const client = getTypedSupabaseClient() as unknown as { rpc: Rpc };
  return client.rpc.bind(client);
}

export async function fetchSessionRoomRules(sessionId: string): Promise<RoomRules | null> {
  const { data, error } = await rpc()('get_session_room_rules', { p_session_id: sessionId });
  return error ? null : parseRoomRules(data);
}

export async function fetchRoomTimer(toolSessionId: string): Promise<RoomTimer | null> {
  const { data, error } = await rpc()('get_room_timer', { p_tool_session_id: toolSessionId });
  return error ? null : parseRoomTimer(data);
}

export async function fetchGuestRoomTimer(
  token: string,
  guestSecret: string,
): Promise<(RoomTimer & { anonymity: RoomAnonymity }) | null> {
  const { data, error } = await rpc()('get_guest_room_timer', { p_token: token, p_guest_secret: guestSecret });
  const timer = error ? null : parseRoomTimer(data);
  if (!timer) return null;
  return { ...timer, anonymity: asAnonymity((data as Record<string, unknown>).anonymity) };
}

/** The host sets the timer (seconds from now) or clears it (null). */
export async function setRoomTimer(
  toolSessionId: string,
  seconds: number | null,
): Promise<{ ok: true; timer: RoomTimer } | { ok: false; error: string }> {
  const { data, error } = await rpc()('set_room_timer', { p_tool_session_id: toolSessionId, p_seconds: seconds ?? 0 });
  if (error) return { ok: false, error: error.message };
  const row = data as Record<string, unknown> | null;
  if (!row || row.success !== true) return { ok: false, error: String(row?.error ?? 'The timer could not be set.') };
  return { ok: true, timer: parseRoomTimer(row) ?? { hasTimer: false, secondsLeft: null, atZero: 'tell' } };
}

/** Tell everyone in the room to read the rules again (the host changed one). */
export async function announceRoomRulesChanged(sessionId: string, actorId: string, actorName: string): Promise<void> {
  await emitSessionEvent(sessionId, actorId, actorName, 'room_rules_changed', {});
}

/** Follow one session's room events of these types; returns the unsubscribe. */
function followRoomEvents(sessionId: string, types: string[], onEvent: () => void): () => void {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return () => {};
  const channel = supabase
    .channel(`room:${types[0]}:${sessionId}:${Math.random().toString(36).slice(2, 8)}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
      (payload) => {
        if (types.includes((payload.new as { event_type?: string }).event_type ?? '')) onEvent();
      },
    )
    .subscribe();
  return () => void supabase.removeChannel(channel);
}

/** The session's room rules, read again whenever the host changes one. */
export function useSessionRoomRules(sessionId: string | null | undefined): { rules: RoomRules; reload: () => void } {
  const [rules, setRules] = useState<RoomRules>(DEFAULT_ROOM_RULES);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    void fetchSessionRoomRules(sessionId).then((r) => {
      if (!cancelled && r) setRules(r);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, tick]);

  useEffect(() => {
    if (!sessionId) return;
    return followRoomEvents(sessionId, ['room_rules_changed'], reload);
  }, [sessionId, reload]);

  return { rules, reload };
}

const TIMER_POLL_MS = 5000;

/**
 * The room's timer for one activity: when it reaches zero in this browser's
 * clock, and what happens then. Polls the server and re-reads at once when
 * the host sets or clears it.
 */
export function useRoomTimer(sessionId: string | null | undefined, toolSessionId: string | null | undefined) {
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [atZero, setAtZero] = useState<TimerAtZero>('tell');

  const refresh = useCallback(async () => {
    if (!toolSessionId) return;
    const timer = await fetchRoomTimer(toolSessionId);
    if (!timer) return;
    setAtZero(timer.atZero);
    setEndsAt((current) => nextTimerEnd(current, timer.hasTimer ? timer.secondsLeft : null));
  }, [toolSessionId]);

  useEffect(() => {
    if (!toolSessionId) {
      setEndsAt(null);
      return;
    }
    void refresh();
    const id = setInterval(() => void refresh(), TIMER_POLL_MS);
    return () => clearInterval(id);
  }, [toolSessionId, refresh]);

  useEffect(() => {
    if (!sessionId) return;
    return followRoomEvents(sessionId, ['timer_set', 'timer_cleared', 'room_rules_changed'], () => void refresh());
  }, [sessionId, refresh]);

  return { endsAt, atZero, refresh, setEndsAt };
}

/** Whether a timer has run out, in this browser's clock. */
export function timerIsUp(endsAt: number | null, nowMs: number = Date.now()): boolean {
  return endsAt !== null && nowMs >= endsAt;
}

/**
 * A guest's view of the room's timer. Realtime is closed to a guest (the same
 * row security that hides the session), so this polls, only while `active`.
 */
export function useGuestRoomTimer(token: string, active: boolean) {
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [atZero, setAtZero] = useState<TimerAtZero>('tell');

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const secret = getOrCreateGuestSecret(token);
    const read = async () => {
      const timer = await fetchGuestRoomTimer(token, secret);
      if (cancelled || !timer) return;
      setAtZero(timer.atZero);
      setEndsAt((current) => nextTimerEnd(current, timer.hasTimer ? timer.secondsLeft : null));
    };
    void read();
    const id = setInterval(() => void read(), TIMER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, active]);

  return { endsAt, atZero };
}

/** Where the room is, for a guest by link (S-8, migration 992). */
export interface GuestRoomState {
  sessionState: string;
  paused: boolean;
  spotlight: { itemId: string; itemTitle: string; note?: string } | null;
  broadcast: { id: string; message: string } | null;
  reveal: Array<{ event_type: string; payload: { rank?: number } | null; created_at?: string }>;
  result: { items: Array<{ id: string; name: string; total: number | null }>; ties: string[][] } | null;
}

export function parseGuestRoomState(row: unknown): GuestRoomState | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  if (r.success !== true) return null;
  const spot = r.spotlight as { itemTitle?: unknown; note?: unknown } | null;
  const b = r.broadcast as { id?: unknown; message?: unknown } | null;
  const res = r.result as { items?: unknown; ties?: unknown } | null;
  return {
    sessionState: typeof r.session_state === 'string' ? r.session_state : '',
    paused: r.paused === true,
    spotlight:
      spot && typeof spot.itemTitle === 'string' && spot.itemTitle
        ? { itemId: spot.itemTitle, itemTitle: spot.itemTitle, ...(typeof spot.note === 'string' && spot.note ? { note: spot.note } : {}) }
        : null,
    broadcast: b && typeof b.id === 'string' && typeof b.message === 'string' && b.message ? { id: b.id, message: b.message } : null,
    reveal: Array.isArray(r.reveal) ? (r.reveal as GuestRoomState['reveal']) : [],
    result:
      res && Array.isArray(res.items)
        ? {
            items: (res.items as Array<{ id?: unknown; name?: unknown; total?: unknown }>).map((i) => ({
              id: String(i.id ?? ''),
              name: String(i.name ?? i.id ?? ''),
              total: typeof i.total === 'number' ? i.total : i.total != null ? Number(i.total) : null,
            })),
            ties: Array.isArray(res.ties) ? (res.ties as string[][]) : [],
          }
        : null,
  };
}

const GUEST_ROOM_POLL_MS = 2000;

/**
 * A guest's view of the room (S-8): pause, spotlight, the host's broadcast,
 * the reveal and the closed result. Realtime is closed to a guest, so this
 * polls the guest room read while `active`.
 */
export function useGuestRoomState(token: string, active: boolean): GuestRoomState | null {
  const [state, setState] = useState<GuestRoomState | null>(null);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const secret = getOrCreateGuestSecret(token);
    const read = async () => {
      const { data, error } = await rpc()('get_guest_room_state', { p_token: token, p_guest_secret: secret });
      if (cancelled || error) return;
      const parsed = parseGuestRoomState(data);
      if (parsed) setState(parsed);
    };
    void read();
    const id = setInterval(() => void read(), GUEST_ROOM_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, active]);
  return state;
}
